import React, { useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Box, Grid
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import { tarefaService, Tarefa } from '../../services/tarefaService';
import FuncionariosSelect from '../selects/FuncionariosSelect';

interface ExecutarTarefaModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  tarefa: Tarefa | undefined;
  referenceDate?: Date;
}

const ExecutarTarefaModal: React.FC<ExecutarTarefaModalProps> = ({ open, onClose, onSuccess, tarefa, referenceDate }) => {
  const { enqueueSnackbar } = useSnackbar();

  const getReferenciaFromDate = (date: Date | string) => {
    if (typeof date === 'string') {
      return date.slice(0, 7);
    }

    return format(date, 'yyyy-MM');
  };
  
  const { control, handleSubmit, watch, reset, formState: { isSubmitting } } = useForm({
    defaultValues: {
      data_execucao: format(new Date(), 'yyyy-MM-dd'),
      tipo_executor: 'funcionario', // 'funcionario' | 'outro'
      usuario_id_executou: '',
      nome_executou: '',
      observacoes: ''
    }
  });

  const tipo_executor = watch('tipo_executor');

  useEffect(() => {
    if (open) {
      reset({
        data_execucao: format(new Date(), 'yyyy-MM-dd'),
        tipo_executor: 'funcionario',
        usuario_id_executou: '',
        nome_executou: '',
        observacoes: ''
      });
    }
  }, [open, reset]);

  const onSubmit = async (data: any) => {
    if (!tarefa) return;
    try {
      const referencia = tarefa.tipo_agendamento === 'recorrente' && referenceDate
        ? getReferenciaFromDate(referenceDate)
        : getReferenciaFromDate(data.data_execucao);

      await tarefaService.marcarExecutada(tarefa.id, {
        data_execucao: data.data_execucao,
        usuario_id_executou: data.tipo_executor === 'funcionario' ? Number(data.usuario_id_executou) : null,
        nome_executou: data.tipo_executor === 'outro' ? data.nome_executou : null,
        observacoes: data.observacoes,
        mes_referencia: referencia
      });
      enqueueSnackbar('Tarefa marcada como executada!', { variant: 'success' });
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Erro ao executar tarefa', { variant: 'error' });
    }
  };

  if (!tarefa) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Executar Tarefa: {tarefa.titulo}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="data_execucao"
                control={control}
                rules={{ required: 'Data é obrigatória' }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="date"
                    label="Data de Execução"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="tipo_executor"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Executado por"
                    fullWidth
                  >
                    <MenuItem value="funcionario">Funcionário</MenuItem>
                    <MenuItem value="outro">Outro</MenuItem>
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              {tipo_executor === 'funcionario' ? (
                <Controller
                  name="usuario_id_executou"
                  control={control}
                  rules={{ required: tipo_executor === 'funcionario' ? 'Selecione um funcionário' : false }}
                  render={({ field, fieldState }) => (
                    <FuncionariosSelect
                      value={field.value ? Number(field.value) : null}
                      onChange={(val) => field.onChange(val)}
                      label="Funcionário"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      required={tipo_executor === 'funcionario'}
                    />
                  )}
                />
              ) : (
                <Controller
                  name="nome_executou"
                  control={control}
                  rules={{ required: tipo_executor === 'outro' ? 'Informe o nome' : false }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Nome do Executor"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="observacoes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Observações da Execução"
                    fullWidth
                    multiline
                    rows={3}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            Marcar como Executada
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ExecutarTarefaModal;
